import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { User, Lock, ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Logo, MotionButton, AuthBackground, DevCredit } from "../components/common";
import { EASE } from "../components/motion";
import { FloatField } from "./Login";

const Card = ({ children }) => (
  <div className="relative min-h-screen overflow-hidden bg-[var(--canvas)] flex flex-col items-center justify-center px-4 py-10">
    <AuthBackground />
    <motion.div initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.6, ease: EASE }} className="glass gradient-border relative z-10 w-full max-w-[440px] rounded-[22px] p-6 sm:p-9">
      <Logo className="mb-6 h-16 w-auto" />
      {children}
      <Link to="/giris" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[var(--ink)] group" data-testid="back-to-login-link">
        <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" /> Girişe dön
      </Link>
    </motion.div>
    <div className="relative z-10 mt-8"><DevCredit /></div>
  </div>
);

export default function ForgotPassword() {
  const [identifier, setIdentifier] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return toast.error("Lütfen giriş bilginizi girin.");
    setLoading(true);
    try {
      const r = await api.post("/auth/forgot-password", { identifier: identifier.trim() });
      setRes(r.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Card>
      <h2 className="text-2xl font-extrabold tracking-tight">Şifremi Unuttum</h2>
      <p className="mt-1.5 text-sm text-slate-500">Hesabınıza kayıtlı e-posta, T.C. Kimlik No veya telefonunuzu girin.</p>
      {res ? (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-6 rounded-xl bg-emerald-50 p-4 text-sm text-emerald-800 ring-1 ring-emerald-100" data-testid="forgot-result">
          <p className="flex items-start gap-2 font-semibold"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> {res.message}</p>
          <p className="mt-2 text-xs text-emerald-700/80">Bağlantı 30 dakika geçerlidir. E-postayı göremiyorsanız gereksiz (spam) klasörünü kontrol edin veya eğitim danışmanınızla iletişime geçin.</p>
        </motion.div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-4">
          <FloatField id="fp" label="E-posta, T.C. Kimlik No veya Telefon" icon={User} value={identifier} onChange={setIdentifier} testid="forgot-identifier-input" />
          <MotionButton type="submit" className="w-full h-12" loading={loading} loadingText="Hazırlanıyor..." data-testid="forgot-submit-btn">
            <KeyRound className="h-4 w-4" /> Sıfırlama Bağlantısı Gönder
          </MotionButton>
        </form>
      )}
    </Card>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [state, setState] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Şifre en az 8 karakter olmalıdır.");
    if (pw !== pw2) return toast.error("Şifreler eşleşmiyor.");
    setState("loading");
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", password: pw });
      setState("success");
      toast.success("Şifreniz güncellendi. Giriş yapabilirsiniz.");
      setTimeout(() => nav("/giris"), 900);
    } catch (err) {
      setState("idle");
      toast.error(errMsg(err));
    }
  };
  return (
    <Card>
      <h2 className="text-2xl font-extrabold tracking-tight">Yeni Şifre Belirleyin</h2>
      <p className="mt-1.5 text-sm text-slate-500">En az 8 karakterden oluşan yeni şifrenizi girin.</p>
      <form onSubmit={submit} className="mt-6 space-y-3.5">
        <FloatField id="np" label="Yeni Şifre" type="password" icon={Lock} value={pw} onChange={setPw} testid="reset-password-input" />
        <FloatField id="np2" label="Yeni Şifre (Tekrar)" type="password" icon={Lock} value={pw2} onChange={setPw2} testid="reset-password-confirm-input" />
        <MotionButton type="submit" className="w-full h-12" loading={state === "loading"} success={state === "success"} data-testid="reset-submit-btn">
          Şifreyi Güncelle
        </MotionButton>
      </form>
    </Card>
  );
}
